import { Injectable } from '@angular/core';
import { AppConstants } from './../../constants/app-constants';
import { AppStartupService } from './../app-startup';
import { IDBService } from '../indexdb/idb.service';
import { Observable, forkJoin } from 'rxjs';
import { LoggingService } from './../logging/logging.service';
import { LoggingConstants } from './../../constants/logging-constant';

@Injectable()
export class CacheStorageService {

    /* istanbul ignore next */
    constructor(private idbStorage: IDBService, private appStartUp: AppStartupService, private loggingService: LoggingService) { }

    /* istanbul ignore next */
    public setItem<T>(key: string, value: T, instanceName: string): Observable<any> {
        let tx = this.createStoreForInstanceName(instanceName);
        if (LoggingService.uConsole) {
            console.log(' In CacheStorageService.setItem()', key, value, instanceName, tx);
        }
        this.loggingService._debug('In CacheStorageService.setItem()' + key + value + instanceName + tx, 'UI Core', LoggingConstants.INFO);
        if (tx === null || tx === undefined) {
            return this.returnBlankObservable();
        } else {
            return this.idbStorage.addRecordsInObjStore(tx.name, value, key);
        }
    }

    /**
     * @param key
     * @returns {any}
     */

    /* istanbul ignore next */
    public getItem<T>(key: string, instanceName: string): Observable<any> {
        let tx = this.createStoreForInstanceName_readOnly(instanceName);
        if (LoggingService.uConsole) {
            console.log('In CacheStorageService.getItem()', key, instanceName, tx);
        }
        this.loggingService._debug('In CacheStorageService.getItem()' + key + instanceName + tx, 'UI Core', LoggingConstants.INFO);
        if (tx === null || tx === undefined) {
            return this.returnBlankObservable();
        } else {
            return this.idbStorage.getRecordFromObjStore(tx.name, key);
        }
    }

    /**
     *
     * @param key
     * @returns {any}
     */

    /* istanbul ignore next */
    public removeItem(key: string, instanceName: string): Observable<any> {
        if (LoggingService.uConsole) {
            console.log('In CacheStorageService.removeItem()', key, instanceName);
        }
        this.loggingService._debug('In CacheStorageService.removeItem()' + key + instanceName, 'UI Core', LoggingConstants.INFO);
        return this.idbStorage.deleteRecordAsync(instanceName, key);
    }


    /* istanbul ignore next */
    public createStoreForInstanceName(instanceName: string) {
        let txDB = this.idbStorage.getDBName();
        if (LoggingService.uConsole) {
            console.log('In CacheStorageService.createStoreForInstanceName():: txDB', txDB);
        }

        try {
            let tx = txDB.transaction(instanceName, 'readwrite');
            tx.oncomplete = function (event) {
                if (LoggingService.uConsole) {
                    console.log('In CacheStorageService.createStoreForInstanceName()::transaction complete', event);
                }
            };

            tx.onerror = function (event) {
                if (LoggingService.uConsole) {
                    console.log('In CacheStorageService.createStoreForInstanceName()::transaction error', event);
                }
            };
            return tx.objectStore(instanceName);
        } catch (err) {
            AppConstants.INDEX_DB_VERSION = AppConstants.INDEX_DB_VERSION + 1;
            if (LoggingService.uConsole) {
                console.log('In error case, this will create new version to add new store. In CacheStorageService.createStoreForInstanceName()', instanceName, AppConstants.INDEX_DB_VERSION, err);
            }
            this.loggingService._debug('In error case, this will create new version to add new store. In CacheStorageService.createStoreForInstanceName()' + instanceName + AppConstants.INDEX_DB_VERSION + err, 'UI Core', LoggingConstants.INFO);
            this.updateSchema(instanceName, AppConstants.INDEX_DB_VERSION);
            return null;
        }
    }

    /**
 * Function for creation of readOnly transaction to Indexed DB.
 */

    /* istanbul ignore next */
    public createStoreForInstanceName_readOnly(instanceName: string) {
        let txDB = this.idbStorage.getDBName();
        if (LoggingService.uConsole) {
            console.log('In CacheStorageService.createStoreForInstanceName_readOnly():: txDB', txDB);
        }
        try {
            let tx = txDB.transaction(instanceName, 'readonly');
            tx.oncomplete = function (event) {
                if (LoggingService.uConsole) {
                    console.log('In CacheStorageService.createStoreForInstanceName_readOnly():transaction complete ', event);
                }
            };

            tx.onerror = function (event) {
                if (LoggingService.uConsole) {
                    console.log('In CacheStorageService.createStoreForInstanceName_readOnly():transaction error', event);
                }
            };
            return tx.objectStore(instanceName);
        } catch (err) {
            AppConstants.INDEX_DB_VERSION = AppConstants.INDEX_DB_VERSION + 1;
            if (LoggingService.uConsole) {
                console.log('In error case, this will create new version to add new store. In CacheStorageService.createStoreForInstanceName()', instanceName, AppConstants.INDEX_DB_VERSION, err);
            }
            this.updateSchema(instanceName, AppConstants.INDEX_DB_VERSION);
            return null;
        }
    }

    /*Updates schema in db */
    /* istanbul ignore next */
    private updateSchema(instanceName: string, version: number) {
        this.idbStorage.openDB(AppConstants.APP_DB_NAME, instanceName, version)
            .subscribe((succ) => {
                if (LoggingService.uConsole) {
                    console.log('In CacheStorageService.updateSchema() - successful', instanceName, version, succ);
                }
                this.loggingService._debug('In CacheStorageService.updateSchema() - successful' + instanceName + version + succ, 'UI Core', LoggingConstants.INFO);
            }, (err) => {
                if (LoggingService.uConsole) {
                    console.log('In CacheStorageService.updateSchema() - failed', instanceName, version, err);
                }
                this.loggingService._debug('In CacheStorageService.updateSchema() - failed' + instanceName + version + err, 'UI Core', LoggingConstants.INFO);
            });
    }


    /*Clear stores given in array, if store does not exist it will throw an empty observable */

    /* istanbul ignore next */
    public clearCache(storeArray: Array<String>): Observable<any> {
        let obsArray = [];
        for (let store of Object.keys(storeArray)) {
            obsArray.push(this.idbStorage.clearObjectStore(storeArray[store]));
        }
        return forkJoin(obsArray);
    }

    /*returns blank observable when no data found */
    /* istanbul ignore next */
    private returnBlankObservable() {
        return new Observable((observer) => {
            observer.next({});
            observer.complete();
        });
    }

    /**
    * Clearing all IndexedDB stores stores purpose
    */
   /* istanbul ignore next */
    public clear_ALLIDBStores() {
        this.idbStorage.clearALL_IDBStores();
    }

    /* istanbul ignore next */
    public clear_ALLIDBStores_SW() {
        this.idbStorage.clearALL_IDBStores_SW();
    }

}




